import React, { useEffect } from "react";

import DateFnsUtils from "@date-io/date-fns";

import { useDispatch, useSelector } from "react-redux";
import { setStartTime } from "redux/TimeSelector/TimeSelector.actions";
import { setDamperConfigs } from "redux/Dampers/Dampers.actions";
import { getUTCTime } from "utils/time.utils";
import { StoreState } from "models";

import { Theme, makeStyles, createStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker, DateTimePicker } from "@material-ui/pickers";
import CustomButton from "./CustomButton";

const localStyles = makeStyles((theme: Theme) =>
  createStyles({
    pickerContainer: {
      marginBottom: "20px",
      display: "flex",
      gap: "20px",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        padding: "2px",
        margin: "10px",
      },
    },
  }),
);

const DateTimePickers = () => {
  const dispatch = useDispatch();
  const localClasses = localStyles();

  const fetchingMillsInvalidated = useSelector((state: StoreState) => state.millSelector.didInvalidate);
  const startDate = useSelector((state: StoreState) => state.timeSelector.startTime);
  const endDate = useSelector((state: StoreState) => state.timeSelector.endTime);
  const localMillTime = useSelector((state: StoreState) => state.timeSelector.localMillTime);
  const loadingMills: boolean = useSelector((state: StoreState) => state.millSelector.fetching);
  const gmtOffset: number = useSelector((state: StoreState) =>
    state.millSelector.selectedMill ? parseInt(state.millSelector.selectedMill.offset) : 0,
  );

  const [startDateTime, setStartDateTime] = React.useState<Date | null>(new Date(getUTCTime()));

  const getTimezoneOffset = () => {
    return `GMT ${gmtOffset < 0 ? "-" : gmtOffset > 0 ? "+" : ""}${gmtOffset ? Math.abs(gmtOffset) / (60 * 60 * 1000) : 0}`;
  };

  useEffect(() => {
    dispatch(setStartTime(new Date(getUTCTime()), gmtOffset));
  }, [dispatch, loadingMills, gmtOffset]);

  const handleDateTimeChange = () => {
    const isValid = startDateTime && !!startDateTime.toJSON();

    if (!isValid) return console.error("invalid date");

    if (startDateTime!.toISOString() !== startDate) {
      dispatch(setDamperConfigs([], true));
      dispatch(setStartTime(startDateTime, gmtOffset));
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div className={localClasses.pickerContainer}>
        <KeyboardDateTimePicker
          required={true}
          id="startTime"
          ampm={false}
          variant="inline"
          format="dd/MM/yyyy HH:mm"
          disabled={fetchingMillsInvalidated}
          label="UTC Start time"
          value={startDateTime}
          onChange={(date) => {
            setStartDateTime(date);
          }}
          fullWidth={true}
          autoOk={true}
          disableFuture
        />
        <CustomButton
          disabled={false}
          text={"Set"}
          handleClick={(event) => handleDateTimeChange()}
        />
      </div>
      <div className={localClasses.pickerContainer}>
        <DateTimePicker
          style={{ width: "100%" }}
          id="endTime"
          ampm={false}
          variant="inline"
          disabled
          format="dd/MM/yyyy HH:mm"
          label="UTC End time"
          value={endDate || null}
          onChange={() => { }}
          fullWidth={true}
        />
      </div>
      <div className={localClasses.pickerContainer}>
        <DateTimePicker
          style={{ width: "100%", border: "0px" }}
          id="localTime"
          ampm={false}
          variant="inline"
          disabled
          format="dd/MM/yyyy HH:mm"
          label={`Mill local standard time ${getTimezoneOffset()}`}
          value={localMillTime || null}
          onChange={() => { }}
          fullWidth={false}
        />
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default DateTimePickers;
